/**
 * Pure geometry helpers for "magnetic" alignment while dragging/resizing
 * objects on the editor canvas. Kept free of Fabric.js types so the maths
 * can be unit tested without a canvas/DOM. All positions are in the same
 * flat px space as Fabric object `left`/`top` (i.e. canvas content space
 * *before* the zoom transform — see `PX_PER_MM` in `scene.ts`).
 */

export type SnapCandidates = { vertical: number[]; horizontal: number[] };

export type SnapMatch = { offset: number; guide: number };

/** Finds the candidate axis position closest to `value`, within `threshold`. */
export function findNearest(value: number, candidates: number[], threshold: number): number | null {
  let best: number | null = null;
  let bestDelta = Infinity;
  for (const candidate of candidates) {
    const delta = Math.abs(candidate - value);
    if (delta <= threshold && delta < bestDelta) {
      bestDelta = delta;
      best = candidate;
    }
  }
  return best;
}

/**
 * Given several candidate edges of the object being dragged (e.g. its
 * left/center/right x-positions) and a pool of target positions to snap to,
 * returns the smallest offset needed to align any one of those edges, plus
 * the absolute guide-line position that matched (for rendering).
 */
export function snapAxis(
  edges: number[],
  candidates: number[],
  threshold: number,
): SnapMatch | null {
  let best: (SnapMatch & { absDelta: number }) | null = null;
  for (const edge of edges) {
    for (const candidate of candidates) {
      const delta = candidate - edge;
      const absDelta = Math.abs(delta);
      if (absDelta <= threshold && (!best || absDelta < best.absDelta)) {
        best = { offset: delta, guide: candidate, absDelta };
      }
    }
  }
  return best ? { offset: best.offset, guide: best.guide } : null;
}

/** Page-level snap targets: edges, safe-margin inset, and center. */
export function pageSnapCandidates(
  originPx: { x: number; y: number },
  sizeMm: { width: number; height: number },
  safeMarginMm: number,
  pxPerMm: number,
): SnapCandidates {
  const left = originPx.x;
  const top = originPx.y;
  const width = sizeMm.width * pxPerMm;
  const height = sizeMm.height * pxPerMm;
  const right = left + width;
  const bottom = top + height;
  const safe = Math.max(0, safeMarginMm) * pxPerMm;
  return {
    vertical: [left, left + safe, left + width / 2, right - safe, right],
    horizontal: [top, top + safe, top + height / 2, bottom - safe, bottom],
  };
}

/** Snap targets contributed by another element's current bounding box. */
export function objectBoundsToCandidates(bounds: {
  left: number;
  top: number;
  width: number;
  height: number;
}): SnapCandidates {
  const right = bounds.left + bounds.width;
  const bottom = bounds.top + bounds.height;
  return {
    vertical: [bounds.left, bounds.left + bounds.width / 2, right],
    horizontal: [bounds.top, bounds.top + bounds.height / 2, bottom],
  };
}

export function mergeCandidates(...groups: SnapCandidates[]): SnapCandidates {
  return {
    vertical: groups.flatMap((g) => g.vertical),
    horizontal: groups.flatMap((g) => g.horizontal),
  };
}
